// src/components/Dialog.tsx

import React, { useState } from "react";

interface DialogProps {
  title: string;
  message: React.ReactNode; 
  onClose: () => void;
  onConfirm?: () => void; // Optional action for OK button
  onCancel?: () => void; // Optional action for Cancel button
  confirmButtonText: string;
  cancelButtonText?: string;
  copyableContent?: string; // Optional raw string to copy to clipboard
}

const Dialog: React.FC<DialogProps> = ({
  title,
  message,
  onClose,
  onConfirm,
  onCancel,
  confirmButtonText,
  cancelButtonText,
  copyableContent,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!copyableContent) return;
    navigator.clipboard.writeText(copyableContent).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 p-4">
      {/*
        The card is capped at 85vh and laid out as a column so the title and the
        buttons stay put while only the message scrolls. Without the cap a long
        message (a month-end Turnover list runs to hundreds of lines) grew the
        card past the viewport in both directions: because the overlay centres
        it, the heading scrolled off the top and the Close button off the
        bottom, with no scrollbar anywhere to reach either.
      */}
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full max-h-[85vh] flex flex-col">
        <h3 className="text-xl font-semibold text-center mb-4 shrink-0">{title}</h3>
        <div className="text-gray-700 text-left mb-6 whitespace-pre-wrap overflow-y-auto flex-1 min-h-0 pr-1">
          {message}
        </div>
        <div className="flex justify-center gap-4 shrink-0">
          {copyableContent && (
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-all"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          )}
          {onCancel && cancelButtonText && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
            >
              {cancelButtonText}
            </button>
          )}
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-[#a6a9ac] text-white rounded-lg hover:bg-[#474747]"
          >
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dialog;
